import { RefObject } from "react";
import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import LayerList from "@arcgis/core/widgets/LayerList";
import Fullscreen from "@arcgis/core/widgets/Fullscreen";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleRenderer from "@arcgis/core/renderers/SimpleRenderer";
import Polygon from "@arcgis/core/geometry/Polygon";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";

interface IPublishedLayer {
  url: string;
  expression: string;
  title: string;
  queryExtent?: boolean;
}

const polygonSymbol = new SimpleFillSymbol({
  color: "blue", // Set the desired fill color with transparency
  outline: {
    // autocasts as new SimpleLineSymbol()
    color: "blue",
    width: 1, // Set the desired outline width
  },
});

// Create a renderer using the symbol
const polygonRenderer = new SimpleRenderer({
  symbol: polygonSymbol,
});

class MapBController {
  map: Map = new Map({ basemap: "topo" });
  mapView: MapView | null = null;

  initializeMap = async (
    domRef: RefObject<HTMLDivElement | null>,
    fullScreenDiv: RefObject<HTMLDivElement | null>,
  ) => {
    if (!domRef.current) return;

    this.map = new Map({
      basemap: "topo",
    });

    this.mapView = new MapView({
      map: this.map,
      container: domRef.current,
      center: [20.80389, 47.75838],
      zoom: 3,
    });

    this.mapView.when(() => {
      if (!this?.mapView) return;
      const layerList = new LayerList({
        view: this?.mapView,
      });

      this.mapView.ui.add(layerList, "top-right");
      if (!fullScreenDiv.current) return;
      this.mapView.ui.add(
        new Fullscreen({
          view: this.mapView,
          element: fullScreenDiv.current,
        }),
        "top-left",
      );
    });
  };

  addLayer = (params: IPublishedLayer) => {
    const { url, expression, title } = params;
    const featureLayer = new FeatureLayer({
      url,
      definitionExpression: expression,
      renderer: polygonRenderer,
      id: title,
      title,
    });

    this.map.add(featureLayer);
  };

  goTo = (featureSet: __esri.FeatureSet) => {
    this.mapView?.goTo({ target: featureSet.features }, { animate: true });
  };

  calculateTotalArea = (featureSet: __esri.FeatureSet | null) => {
    if (!featureSet) return null;
    let totalArea = 0;
    const totalPoints = featureSet.features.length;

    let totalAreaGeodesic = 0;
    featureSet.features.forEach((feature) => {
      totalArea += feature.attributes.Shape__Area;
      totalAreaGeodesic += geometryEngine.geodesicArea(
        feature.geometry as Polygon,
        "square-kilometers",
      );
    });

    // Calculate average area
    // const totalAreaKm2 = totalArea / 1_000_000;
    // const averageAreaKm2 = totalAreaKm2 / features.length;
    // const averageAreaKm2Rounded = parseFloat(averageAreaKm2.toFixed(1));
    const averageAreaKm2Geodesic =
      totalAreaGeodesic / featureSet.features.length;
    const averageAreaKm2GeodesicRounded = Number.parseFloat(
      averageAreaKm2Geodesic.toFixed(1),
    );
    return {
      averageAreaKm2GeodesicRounded,
      totalPoints,
      totalArea,
    };
  };

  removeAllLayers = () => {
    this.mapView?.map.removeAll();
  };

  destroyMap = () => {
    if (this.mapView) {
      this.mapView.destroy();
    }
  };
}

export const mapBController = new MapBController();
